import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type AnalyticsTypeCount = {
  type: string;
  count: number;
  /** Distinct users. Absent means unknown, never zero. */
  people?: number;
};

export type AnalyticsWindow = {
  window: string;
  total: number;
  byType: AnalyticsTypeCount[];
};

export const BETA_USAGE_FORMAT = 2;

export type BetaFeatureDef = {
  id: string;
  name: string;
  keyAction1: string;
  keyAction2: string;
  metrics: string[];
};

export type BetaDay = {
  day: string;
  counts: Record<string, number>;
};

export type BetaUser = {
  sub: string;
  /** Every sign-in the person's events arrived under, newest first. */
  subs: string[];
  userId?: string;
  districtId?: string;
  days: BetaDay[];
};

export type BetaUsage = {
  format: typeof BETA_USAGE_FORMAT;
  features: BetaFeatureDef[];
  users: BetaUser[];
  unattributed: number;
};

export type BetaAccessEntry = {
  sub: string;
  /** The stable identity behind the sub, when the grant recorded one. */
  userId?: string;
  feature: string;
  grantedAt: string;
};

export type BetaAccess = {
  entries: BetaAccessEntry[];
};

export type DistrictStat = {
  districtId: string;
  name?: string;
  count: number;
  p90Ms: number;
  maxMs: number;
  live: number;
  liveLoads: number;
};

export type RouteStats = {
  route: string;
  count: number;
  failures: number;
  unconfigured: number;
  p50Ms: number;
  p90Ms: number;
  maxMs: number;
  live: number;
  liveLoads: number;
  slowest: DistrictStat[];
};

export type ProbeStats = {
  routes: RouteStats[];
  lastRunAt?: string;
  districts: number;
};

export type CacheDistrictStat = {
  districtId: string;
  name?: string;
  reads: number;
  oldestAgeMs: number;
};

/** How the job behind a cache ran over the window; the audit reports its latest run instead. */
export type RefresherStats = {
  route: string;
  runs: number;
  failures: number;
  lastSuccessAt?: string;
  lastFailureAt?: string;
  districts: number;
  carried: number;
  /** The audit's latest report is a plan still filling, or one whose shards never all started. */
  incomplete?: boolean;
};

/** One cache graded against its contract over the loads people waited for in the window. */
export type CacheStats = {
  cache: string;
  /** How old served data may be before a read is past contract; 0 for a cache graded by hit rate alone. */
  contractMs: number;
  reads: number;
  hits: number;
  misses: number;
  /** Reads that carried the age of the data they served: every hit, plus loads that mixed cached answers with live misses. */
  aged: number;
  p50AgeMs: number;
  p90AgeMs: number;
  oldestAgeMs: number;
  withinContract: number;
  oldestAsOf?: string;
  oldestReadAt?: string;
  oldestDistrict?: string;
  pastContract: CacheDistrictStat[];
  refresher?: RefresherStats;
};

export type PerformanceWindow = {
  window: string;
  users: RouteStats[];
  probe: ProbeStats;
  /** Runs nobody waited for; undefined for a snapshot written before they were kept apart. */
  background?: RouteStats[];
  /** Undefined for a snapshot written before cache ages were kept. */
  caches?: CacheStats[];
};

export type AccessRoleCount = {
  total: number;
  granted: number;
};

export type AccessDistrictCounts = {
  total: number;
  withAccess: number;
  withAdminAccess: number;
  withTeacherAccess: number;
};

export type AccessDistrict = {
  districtId: string;
  districtName: string;
  admins: AccessRoleCount;
  teachers: AccessRoleCount;
};

/** Who holds has_bamboo_access, counted by the FL360 side at rollup time. */
export type AccessCensus = {
  districts: AccessDistrictCounts;
  admins: AccessRoleCount;
  teachers: AccessRoleCount;
  /** Only districts with at least one grant, by name. */
  byDistrict: AccessDistrict[];
};

export type AnalyticsSnapshot = {
  computed: boolean;
  computedAt?: string;
  scanned?: number;
  windows: AnalyticsWindow[];
  betaUsage?: BetaUsage;
  /** Null means the access list could not be read, which is not an empty list. */
  betaAccess: BetaAccess | null;
  /** Undefined for a snapshot written before load times were kept. */
  performance?: PerformanceWindow[];
  /** Undefined for a snapshot written before the census, or when FL360 could not be asked. */
  access?: AccessCensus;
};

type RawRouteStats = Partial<Omit<RouteStats, 'slowest'>> & { slowest?: (Partial<DistrictStat> | null)[] | null };
type RawCacheStats = Partial<Omit<CacheStats, 'pastContract' | 'refresher'>> & {
  pastContract?: (Partial<CacheDistrictStat> | null)[] | null;
  refresher?: Partial<RefresherStats> | null;
};
type RawPerformanceWindow = Partial<Omit<PerformanceWindow, 'users' | 'probe' | 'background' | 'caches'>> & {
  users?: (RawRouteStats | null)[] | null;
  probe?: (Partial<Omit<ProbeStats, 'routes'>> & { routes?: (RawRouteStats | null)[] | null }) | null;
  background?: (RawRouteStats | null)[] | null;
  caches?: (RawCacheStats | null)[] | null;
};

type RawBetaUsage = {
  format?: unknown;
  features?: (Partial<BetaFeatureDef> | null)[] | null;
  users?: (Partial<Omit<BetaUser, 'days'>> & { days?: (Partial<BetaDay> | null)[] | null } | null)[] | null;
  unattributed?: number;
};

type RawBetaAccess = { entries?: (Partial<BetaAccessEntry> | null)[] | null } | null;

type RawRoleCount = Partial<AccessRoleCount> | null;
type RawAccessCensus = {
  districts?: Partial<AccessDistrictCounts> | null;
  admins?: RawRoleCount;
  teachers?: RawRoleCount;
  byDistrict?: (Partial<Omit<AccessDistrict, 'admins' | 'teachers'>> & { admins?: RawRoleCount; teachers?: RawRoleCount } | null)[] | null;
} | null;

type RawSnapshot = Partial<Omit<AnalyticsSnapshot, 'betaUsage' | 'betaAccess' | 'performance' | 'access'>> & {
  access?: RawAccessCensus;
  windows?: (Partial<AnalyticsWindow> | null)[] | null;
  betaUsage?: RawBetaUsage | null;
  betaAccess?: RawBetaAccess;
  performance?: (RawPerformanceWindow | null)[] | null;
};

const ms = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0);

function routeStatsOf(raw: (RawRouteStats | null)[] | null | undefined): RouteStats[] {
  return (raw ?? []).flatMap((r) =>
    r?.route
      ? [
          {
            route: r.route,
            count: ms(r.count),
            failures: ms(r.failures),
            unconfigured: ms(r.unconfigured),
            p50Ms: ms(r.p50Ms),
            p90Ms: ms(r.p90Ms),
            maxMs: ms(r.maxMs),
            live: ms(r.live),
            liveLoads: ms(r.liveLoads),
            slowest: (r.slowest ?? []).flatMap((d) =>
              d?.districtId
                ? [{ districtId: d.districtId, ...(d.name ? { name: d.name } : {}), count: ms(d.count), p90Ms: ms(d.p90Ms), maxMs: ms(d.maxMs), live: ms(d.live), liveLoads: ms(d.liveLoads) }]
                : [],
            ),
          },
        ]
      : [],
  );
}

function refresherOf(raw: Partial<RefresherStats> | null | undefined): RefresherStats | undefined {
  if (!raw?.route) return undefined;
  return {
    route: raw.route,
    runs: ms(raw.runs),
    failures: ms(raw.failures),
    ...(raw.lastSuccessAt ? { lastSuccessAt: raw.lastSuccessAt } : {}),
    ...(raw.lastFailureAt ? { lastFailureAt: raw.lastFailureAt } : {}),
    districts: ms(raw.districts),
    carried: ms(raw.carried),
    ...(raw.incomplete === true ? { incomplete: true } : {}),
  };
}

/** A snapshot written before caches were graded counted hits alone, so its reads are its hits. */
function cacheStatsOf(raw: (RawCacheStats | null)[]): CacheStats[] {
  return raw.flatMap((c) => {
    if (!c?.cache) return [];
    const refresher = refresherOf(c.refresher);
    return [
      {
        cache: c.cache,
        contractMs: ms(c.contractMs),
        reads: ms(c.reads),
        hits: ms(c.hits ?? c.reads),
        misses: ms(c.misses),
        aged: ms(c.aged ?? c.hits ?? c.reads),
        p50AgeMs: ms(c.p50AgeMs),
        p90AgeMs: ms(c.p90AgeMs),
        oldestAgeMs: ms(c.oldestAgeMs),
        withinContract: ms(c.withinContract),
        ...(c.oldestAsOf ? { oldestAsOf: c.oldestAsOf } : {}),
        ...(c.oldestReadAt ? { oldestReadAt: c.oldestReadAt } : {}),
        ...(c.oldestDistrict ? { oldestDistrict: c.oldestDistrict } : {}),
        pastContract: (c.pastContract ?? []).flatMap((d) =>
          d?.districtId ? [{ districtId: d.districtId, ...(d.name ? { name: d.name } : {}), reads: ms(d.reads), oldestAgeMs: ms(d.oldestAgeMs) }] : [],
        ),
        ...(refresher ? { refresher } : {}),
      },
    ];
  });
}

function performanceOf(raw: (RawPerformanceWindow | null)[] | null | undefined): PerformanceWindow[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.flatMap((w) =>
    w?.window
      ? [
          {
            window: w.window,
            users: routeStatsOf(w.users),
            probe: { routes: routeStatsOf(w.probe?.routes), ...(w.probe?.lastRunAt ? { lastRunAt: w.probe.lastRunAt } : {}), districts: ms(w.probe?.districts) },
            ...(Array.isArray(w.background) ? { background: routeStatsOf(w.background) } : {}),
            ...(Array.isArray(w.caches) ? { caches: cacheStatsOf(w.caches) } : {}),
          },
        ]
      : [],
  );
}

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;

function countsOf(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  return Object.fromEntries(Object.entries(raw as Record<string, unknown>).flatMap(([metric, value]) => (isCount(value) ? [[metric, value]] : [])));
}

function betaUsageOf(raw: RawBetaUsage | null | undefined): BetaUsage | undefined {
  if (!raw || raw.format !== BETA_USAGE_FORMAT) return undefined;
  return {
    format: BETA_USAGE_FORMAT,
    features: (raw.features ?? []).flatMap((f) =>
      f?.id ? [{ id: f.id, name: f.name ?? f.id, keyAction1: f.keyAction1 ?? '', keyAction2: f.keyAction2 ?? '', metrics: f.metrics ?? [] }] : [],
    ),
    users: (raw.users ?? []).flatMap((u) =>
      u?.sub
        ? [
            {
              sub: u.sub,
              subs: [...new Set([u.sub, ...(u.subs ?? []).filter((alias): alias is string => typeof alias === 'string' && alias !== '')])],
              userId: u.userId,
              districtId: u.districtId,
              days: (u.days ?? []).flatMap((d) => (d?.day ? [{ day: d.day, counts: countsOf(d.counts) }] : [])),
            },
          ]
        : [],
    ),
    unattributed: raw.unattributed ?? 0,
  };
}

function betaAccessOf(raw: RawBetaAccess | undefined): BetaAccess | null {
  if (!raw || !Array.isArray(raw.entries)) return null;
  return {
    entries: raw.entries.flatMap((e) =>
      e?.sub && e.feature ? [{ sub: e.sub, userId: typeof e.userId === 'string' && e.userId !== '' ? e.userId : undefined, feature: e.feature, grantedAt: e.grantedAt ?? '' }] : [],
    ),
  };
}

const roleCountOf = (raw: RawRoleCount | undefined): AccessRoleCount => ({ total: ms(raw?.total), granted: ms(raw?.granted) });

function accessOf(raw: RawAccessCensus | undefined): AccessCensus | undefined {
  if (!raw || !raw.districts || !raw.admins || !raw.teachers) return undefined;
  return {
    districts: {
      total: ms(raw.districts.total),
      withAccess: ms(raw.districts.withAccess),
      withAdminAccess: ms(raw.districts.withAdminAccess),
      withTeacherAccess: ms(raw.districts.withTeacherAccess),
    },
    admins: roleCountOf(raw.admins),
    teachers: roleCountOf(raw.teachers),
    byDistrict: (raw.byDistrict ?? []).flatMap((d) =>
      d?.districtId ? [{ districtId: d.districtId, districtName: d.districtName ?? '', admins: roleCountOf(d.admins), teachers: roleCountOf(d.teachers) }] : [],
    ),
  };
}

export async function fetchAnalytics(): Promise<AnalyticsSnapshot> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/admin/analytics`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`admin-analytics fetch failed: ${response.status}`);
  }
  const data = ((await response.json()) as RawSnapshot | null) ?? {};
  return {
    computed: data.computed === true,
    computedAt: data.computedAt,
    scanned: data.scanned,
    windows: (data.windows ?? []).flatMap((window) =>
      window ? [{ window: window.window ?? '', total: window.total ?? 0, byType: window.byType ?? [] }] : [],
    ),
    betaUsage: betaUsageOf(data.betaUsage),
    betaAccess: betaAccessOf(data.betaAccess),
    performance: performanceOf(data.performance),
    access: accessOf(data.access),
  };
}
